import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import type { PracticeMarkResult } from '../../utils/markDisplay.ts';
import {
  formatMarkFraction,
  verdictColor,
  verdictLabel,
} from '../../utils/markDisplay.ts';

export type MarkDisputeState = {
  status: 'recheck_done' | 'human_review' | 'resolved' | 'dismissed' | 'accepted';
  message?: string;
  contestId?: string;
  recheckResult?: PracticeMarkResult;
  comment?: string;
};

type DialogStep = 'comment' | 'rechecking' | 'result' | 'sending';

type Props = {
  markResult: PracticeMarkResult;
  dispute: MarkDisputeState | null;
  submitting: boolean;
  onRecheck: (comment: string) => Promise<{ mark: PracticeMarkResult; noEscalation?: boolean }>;
  onAcceptRecheck: (recheck: PracticeMarkResult) => Promise<void>;
  onSendToTutor: (comment: string, recheck: PracticeMarkResult | null) => Promise<void>;
};

const MarkDisputePanel: React.FC<Props> = ({
  markResult,
  dispute,
  submitting,
  onRecheck,
  onAcceptRecheck,
  onSendToTutor,
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<DialogStep>('comment');
  const [comment, setComment] = useState('');
  const [recheckResult, setRecheckResult] = useState<PracticeMarkResult | null>(null);
  const [noEscalation, setNoEscalation] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (dispute?.status === 'recheck_done' && dispute.recheckResult) {
      setRecheckResult(dispute.recheckResult);
      setComment(dispute.comment ?? '');
      setStep('result');
    }
  }, [dispute]);

  const resetDialog = () => {
    setStep('comment');
    setComment(dispute?.comment ?? '');
    setRecheckResult(dispute?.recheckResult ?? null);
    setNoEscalation(false);
    setError(null);
  };

  const openDialog = () => {
    resetDialog();
    if (dispute?.status === 'recheck_done' && dispute.recheckResult) {
      setStep('result');
      setRecheckResult(dispute.recheckResult);
      setComment(dispute.comment ?? '');
    }
    setOpen(true);
  };

  const closeDialog = () => {
    if (submitting) return;
    setOpen(false);
  };

  const handleRecheck = async () => {
    setError(null);
    setStep('rechecking');
    try {
      const { mark, noEscalation: noEsc } = await onRecheck(comment.trim());
      setRecheckResult(mark);
      setNoEscalation(!!noEsc);
      setStep('result');
    } catch (e) {
      setStep('comment');
      setError(e instanceof Error ? e.message : 'Re-check failed. Please try again.');
    }
  };

  const handleAccept = async () => {
    if (!recheckResult) return;
    setError(null);
    try {
      await onAcceptRecheck(recheckResult);
      setOpen(false);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not accept mark.');
    }
  };

  const handleSendToTutor = async () => {
    setError(null);
    setStep('sending');
    try {
      await onSendToTutor(comment.trim(), recheckResult);
      setOpen(false);
    } catch (e) {
      setStep('result');
      setError(e instanceof Error ? e.message : 'Could not send to tutor.');
    }
  };

  if (dispute?.status === 'human_review') {
    return (
      <Alert severity="info" sx={{ mt: 1.5 }}>
        {dispute.message ??
          'Your dispute has been sent to a tutor. You can track it under Mark disputes.'}
      </Alert>
    );
  }

  if (dispute?.status === 'accepted') {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        You accepted the re-checked mark.
      </Typography>
    );
  }

  if (dispute?.status === 'resolved' || dispute?.status === 'dismissed') {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
        A previous dispute on this question was {dispute.status}.
      </Typography>
    );
  }

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Think the marking is wrong? We&apos;ll re-check with a stronger AI marker first, then you
        can still send it to a tutor if you disagree.
      </Typography>
      <Button
        variant="outlined"
        color="warning"
        size="small"
        startIcon={<GavelOutlinedIcon />}
        onClick={openDialog}
        disabled={submitting}
      >
        {dispute?.status === 'recheck_done' ? 'Continue dispute' : 'Dispute mark'}
      </Button>

      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {step === 'result' || step === 'sending' ? 'Re-check result' : 'Dispute this mark'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {(step === 'comment' || step === 'rechecking') && (
              <>
                <Typography variant="body2" color="text.secondary">
                  Current mark:{' '}
                  <strong>{formatMarkFraction(markResult.awarded, markResult.max)}</strong>
                </Typography>
                <Typography variant="body2">
                  Step 1: re-check with a stronger AI marker (nothing is sent to a tutor yet). Step 2:
                  if you still disagree, choose &ldquo;Still disagree — send to tutor&rdquo;.
                </Typography>
                <TextField
                  label="Why do you disagree? (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  multiline
                  minRows={3}
                  disabled={step === 'rechecking'}
                  placeholder="e.g. My equation is balanced; states of matter were not required…"
                />
              </>
            )}

            {step === 'rechecking' && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Re-checking with a stronger AI marker…</Typography>
              </Stack>
            )}

            {(step === 'result' || step === 'sending') && recheckResult && (
              <>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      Original:{' '}
                      <strong>{formatMarkFraction(markResult.awarded, markResult.max)}</strong>
                    </Typography>
                    <Typography variant="body2">
                      Re-checked:{' '}
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: verdictColor(recheckResult.verdict), fontWeight: 700 }}
                      >
                        {formatMarkFraction(recheckResult.awarded, recheckResult.max)}
                      </Typography>{' '}
                      ({verdictLabel(recheckResult.verdict)})
                    </Typography>
                  </Stack>
                </Box>
                {noEscalation && (
                  <Alert severity="info">
                    No stronger AI tier was available. You can still request tutor review below.
                  </Alert>
                )}
                {recheckResult.feedback?.trim() && (
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      AI feedback
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {recheckResult.feedback}
                    </Typography>
                  </Box>
                )}
                <Typography variant="body2" color="text.secondary">
                  Accept the re-checked mark, or send your dispute to a tutor for a final decision.
                </Typography>
              </>
            )}

            {step === 'sending' && (
              <Stack direction="row" spacing={1.5} alignItems="center">
                <CircularProgress size={20} />
                <Typography variant="body2">Sending to tutor queue…</Typography>
              </Stack>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          {step === 'comment' && (
            <>
              <Button onClick={closeDialog} disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => void handleRecheck()}
                disabled={submitting}
              >
                Re-check with AI
              </Button>
            </>
          )}
          {step === 'rechecking' && (
            <Button onClick={closeDialog} disabled={submitting}>
              Cancel
            </Button>
          )}
          {(step === 'result' || step === 'sending') && (
            <>
              <Button onClick={closeDialog} disabled={submitting}>
                Close
              </Button>
              <Button
                variant="outlined"
                onClick={() => void handleAccept()}
                disabled={submitting || step === 'sending'}
              >
                Accept mark
              </Button>
              <Button
                variant="contained"
                color="warning"
                onClick={() => void handleSendToTutor()}
                disabled={submitting || step === 'sending'}
              >
                Still disagree — send to tutor
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MarkDisputePanel;
